import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Alert } from "@/core/components/ui/alert/alert";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { DocumentDropPane } from "./document-drop-pane";
import { DocumentList } from "./document-list";
import {
  UPLOAD_ITEM_STATE,
  type OnboardingDocument,
  type OnboardingDocumentType,
} from "./types";
import { UploadConflictModal } from "./upload-conflict-modal";
import { UploadProgress } from "./upload-progress";
import {
  MAX_FILES_PER_BATCH,
  useDocumentBatchUpload,
} from "./use-document-batch-upload";

const MAX_UPLOAD_SIZE = 20 * 1024 * 1024;

interface DocumentSectionProps {
  businessId: string;
  type: OnboardingDocumentType;
  documents: OnboardingDocument[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

// The uploader and everything sent so far, as one list. Files go up the moment they are
// chosen: the page header already said what to send, so a second click on a button that
// spends most of its life disabled adds nothing.
export const DocumentSection = ({
  businessId,
  type,
  documents,
  isLoading,
  isError,
}: DocumentSectionProps) => {
  const batch = useDocumentBatchUpload(businessId, type);
  const canRetry =
    !batch.isUploading &&
    batch.items.some(
      ({ status }) =>
        status.state === UPLOAD_ITEM_STATE.failed ||
        status.state === UPLOAD_ITEM_STATE.skipped,
    );
  const hasContents =
    batch.items.length > 0 || !!documents?.length || isLoading || isError;

  return (
    <>
      <DocumentDropPane
        maxFiles={MAX_FILES_PER_BATCH}
        maxSize={MAX_UPLOAD_SIZE}
        disabled={batch.isUploading}
        onFiles={batch.uploadAll}
        hasContents={hasContents}
      >
        {isLoading && (
          <Stack align="center" fullWidth className="py-4">
            <LoadingSpinner />
          </Stack>
        )}
        {isError && (
          <Stack className="py-3" fullWidth>
            <Alert variant="destructive">
              We could not load your documents. Refresh the page to try again.
            </Alert>
          </Stack>
        )}
        {batch.items.length > 0 && <UploadProgress items={batch.items} />}
        {canRetry && (
          <Stack direction="row" justify="end" fullWidth className="py-1">
            <Button
              variant="link"
              size="xs"
              leftIcon={<ArrowPathIcon className="h-4 w-4" />}
              onClick={batch.retry}
            >
              Try again
            </Button>
          </Stack>
        )}
        {documents && documents.length > 0 && (
          <DocumentList businessId={businessId} documents={documents} />
        )}
      </DocumentDropPane>

      <UploadConflictModal
        conflict={batch.conflict}
        onCancel={() => batch.answerConflict(null)}
        onResolve={(resolution) => batch.answerConflict(resolution)}
      />
    </>
  );
};
